import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatEUR } from "../lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";

const MAX_ATTEMPTS = 10;
const INTERVAL_MS = 2000;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tx, setTx] = useState(null);
  const { clear } = useCart();
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    document.title = "Pedido confirmado · Mimes Gourmet";
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setTx(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("success");
          if (!clearedRef.current) {
            clearedRef.current = true;
            clear();
          }
          return;
        }
        if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-24 text-center">
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-[color:var(--brand-sage)]" />
          <h1 className="mt-6 font-serif text-3xl text-[color:var(--brand-primary)]">Confirmando tu pedido…</h1>
          <p className="mt-3 text-sm text-[color:var(--brand-neutral)]">Un momento, por favor.</p>
        </div>
      )}

      {status === "success" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="w-14 h-14 mx-auto text-[color:var(--brand-sage)]" strokeWidth={1.2} />
          <p className="eyebrow mt-6">Pedido confirmado</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 text-[color:var(--brand-primary)] font-light">
            ¡Gracias por <span className="italic text-[color:var(--brand-terracotta)]">mimarte</span>!
          </h1>
          <p className="mt-6 text-base text-[color:var(--brand-neutral)] leading-relaxed">
            Tu pedido de <strong>{formatEUR(tx?.amount || 0)}</strong> ha sido confirmado. Recibirás un email de confirmación en breve.
          </p>
          <p className="mt-2 text-xs text-[color:var(--brand-neutral)]/60">Ref: {sessionId?.slice(-12)}</p>
          <div className="mt-10">
            <Link to="/tienda">
              <Button className="bg-[color:var(--brand-primary)] hover:bg-black text-white rounded-md px-8 py-6">
                Seguir descubriendo sabores
              </Button>
            </Link>
          </div>
        </div>
      )}

      {(status === "failed" || status === "timeout" || status === "error") && (
        <div data-testid="payment-error">
          <h1 className="font-serif text-3xl text-[color:var(--brand-primary)]">Algo no fue bien</h1>
          <p className="mt-4 text-sm text-[color:var(--brand-neutral)]">
            No pudimos confirmar el pago. Si el importe se cargó en tu cuenta, contáctanos.
          </p>
          <div className="mt-8">
            <Link to="/tienda">
              <Button variant="outline" className="rounded-md">Volver a la tienda</Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
